import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, merge, Observable, retry, shareReplay, Subject, switchMap, tap, timer } from 'rxjs';
import { IObjectStatus, IObjectStatusParsed, IPositionParsed, ISensorValueParsed } from '../object-history-common.models';
import { UsersService, UserViewModel } from 'app/services/users.service';
import { TimeZoneConverterService } from './time-converter-service';

@Injectable({ providedIn: 'root' })
export class ObjectStatusService {
  private converter = inject(TimeZoneConverterService);
  private usersService = inject(UsersService);
  public user: UserViewModel;
  constructor(
    private http: HttpClient,
  ) {
    this.user = this.usersService.getCurrentUser();
  }

  public getObjectStatus(): Observable<IObjectStatusParsed> {
    return this.http.get<IObjectStatus>('/ctrl/ObjectStatus').pipe(
      map(status => this.parseStatus(status))
    );
  }

  private parseStatus(status: IObjectStatus): IObjectStatusParsed {
    // 1) Normalize & localize times as before (array → array)
    const positionsParsed = (status.positions ?? []).map(p => ({
      ...p,
      time: this.converter.toLocal(p.time, this.user.TimeZone),
    }));

    const sensorValuesParsed = (status.sensorValues ?? []).map(sv => ({
      ...sv,
      time: this.converter.toLocal(sv.time, this.user.TimeZone),
    }));

    // 2) Convert arrays to Maps keyed by objectId
    // Note: if there are duplicate objectIds, the last one wins
    const positionsMap = new Map<number, IPositionParsed>(
      positionsParsed.map(p => [p.objectId, p] as const)
    );

    const sensorValuesMap = new Map<number, ISensorValueParsed>(
      sensorValuesParsed.map(sv => [sv.sensorId, sv] as const)
    );

    return {
      ...status,
      positions: positionsMap,
      sensorValues: sensorValuesMap,
    };
  }

  public manualRefresh$ = new Subject<void>();
  private _currentStatus: IObjectStatusParsed | null = null;

  readonly objectStatus$: Observable<IObjectStatusParsed> = merge(
    timer(0, 30_000),         // immediately, then every 30s
    this.manualRefresh$       // plus any manual triggers
  ).pipe(
    // call your original request
    switchMap(() => this.getObjectStatus()),
    // optional resilience
    retry({ count: 2, delay: 1000 }),
    tap(s => (this._currentStatus = s)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  get currentStatus(): IObjectStatusParsed | null {
    return this._currentStatus;
  }

  public refreshObjectStatusNow() { this.manualRefresh$.next(); }
}
